import React, { Component } from 'react';
import { Form } from '@ant-design/compatible';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {
    DatePicker, Select, Button,
    Input, Row, Col,
    notification,
} from 'antd';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Erase, Calendar } from '@carbon/icons-react';
import * as assetActions from '../../../core/asset/assetActions';
import { secondsToHms } from '../../../core/utils/functions';
import { MapMode } from '../../../types/types';

dayjs.extend(customParseFormat);

const {
    MAP_SLIDER_INTERVAL, DB_DATE_FORMAT,
} = require('../../../core/constants').default;

class VideoSearchFilterForm extends Component {
    constructor(props) {
        super(props);

        this.state = {
            locationSearch: false,
            val: '',
            latitude: null,
            longitude: null,
            geometry: null,
            loadScript: true,
        };
    }

    componentDidMount() {
        const { actions } = this.props;

        actions.getDivisionListRequest();
        actions.getAssetOptionsRequest();
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        // if somehow the map or the location search box is triggered, don't allow the location search box to load script again
        if (
            (!prevState.locationSearch && this.state.locationSearch)
            || (!prevProps.locationSearch && this.props.locationSearch)
        ) {
            this.setState({ loadScript: false });
        }
    }

    onUpdate = (place) => {
        const {
            formatted_address,
            geometry,
        } = place;
        const latitude = geometry.location.lat();
        const longitude = geometry.location.lng();
        this.setState({
            latitude,
            longitude,
            geometry,
        });
        const { form: { setFieldsValue } } = this.props;
        setFieldsValue({
            location: formatted_address,
        });
    };

    submitForm = (e) => {
        if (e) {
            e.preventDefault();
        }
        const {
            latitude,
            longitude,
            geometry,
        } = this.state;
        const {
            onSubmit,
            form: { validateFields, getFieldValue },
        } = this.props;

        // We must validate time here as form custom validation is no longer support by Antd and doesn't seem to work
        // due to the fact that we use the old forms from antd v3.
        // Additionally I am unable to do anything at a component level due to the fact that we use plain text inputs to 
        // present times, and not time picker, so unlike with DatePicker we get no built-in support for validation.
        const timeFrom = getFieldValue('time_from');
        const timeTo = getFieldValue('time_to');
        if (timeFrom && timeTo) {
            const timeFromAsDate = dayjs(`2020/01/01 ${timeFrom}`);
            const timeToAsDate = dayjs(`2020/01/01 ${timeTo}`);
            if (timeFromAsDate.isAfter(timeToAsDate)) {
                notification.error({
                    message: 'Error',
                    description: 'Time from cannot be after time to'
                });
                return;
            }
            if (timeToAsDate.isBefore(timeFromAsDate)) {
                notification.error({
                    message: 'Error',
                    description: 'Time to cannot be before time from'
                });
                return;
            }
        }

        validateFields((err, values) => {
            if (!err) {
                const newValues = values;
                newValues.latitude = latitude;
                newValues.longitude = longitude;
                newValues.geometry = geometry;
                onSubmit(newValues);
            }
        });
    };

    setLocationSearch = (e) => {
        const locationSearch = e.target.checked;

        this.setState({ locationSearch });

        if (!locationSearch) {
            this.setState({
                latitude: null,
                longitude: null,
                geometry: null,
            });
        }
    };

    render() {
        const {
            form,
            divisions,
            driverList,
            assetOptions,
            mapSearchVideoSelectedDate,
            mapSearchVideoSliderValue,
            params,
            videoRetentionDays,
            videoSearchFilters,
            mapSearchVideoMapMode,
            mapSearchSelectedAssetId,
        } = this.props;
        const { getFieldDecorator } = form;

        let dateInitialVal = dayjs();
        let timeFromInitialVal = dayjs().subtract(1, 'hour').format('HH:mm');
        let timeToInitialVal = dayjs().format('HH:mm');
        if (params?.assetId === 'location') {
            let secondsSinceMidnight = mapSearchVideoSliderValue * 10;
            if (mapSearchVideoMapMode === MapMode.Live) {
                secondsSinceMidnight *= 60;
            }
            const SECONDS_IN_AN_HOUR = 3600;
            let timeFrom = secondsSinceMidnight - (1 * SECONDS_IN_AN_HOUR);
            if (timeFrom < 0) {
                timeFrom = 0;
            }
            timeFromInitialVal = dayjs(mapSearchVideoSelectedDate).startOf("day").add(timeFrom, "seconds").format("HH:mm");
            const timeTo = secondsSinceMidnight;
            timeToInitialVal = dayjs(mapSearchVideoSelectedDate).startOf("day").add(timeTo, "seconds").format("HH:mm");
            dateInitialVal = dayjs(mapSearchVideoSelectedDate);
        }

        return (
            (
                <Form onSubmit={this.submitForm} layout="vertical">
                    <Form.Item label="Asset">
                        {getFieldDecorator('asset_id', {
                            initialValue: videoSearchFilters?.search?.asset_id || (mapSearchSelectedAssetId ? [mapSearchSelectedAssetId] : []),
                        })(
                            <Select
                                mode="multiple"
                                showSearch
                                allowClear={<Erase size={12} />}
                                filterOption={(txt, obj) => (obj.props.children.toLowerCase().includes(txt.toLowerCase()))}
                                placeholder="Select Asset"
                            >
                                {assetOptions && assetOptions.map((a) => (
                                    <Select.Option key={a.id} value={a.id}>
                                        {a.name === a.reg || a.reg === '' ? a.name : `${a.name} [${a.reg}]`}
                                    </Select.Option>
                                ))}
                            </Select>,
                        )}
                    </Form.Item>
                    <Form.Item label="Driver">
                        {getFieldDecorator('driver', {
                            initialValue: videoSearchFilters?.search?.driver || [],
                        })(
                            <Select
                                mode="multiple"
                                filterOption={(input, option) => option.props.children.toLowerCase()
                                    .indexOf(input.toLowerCase()) >= 0}
                                placeholder="Select Driver"
                                allowClear={<Erase size={12} />}
                                showSearch
                            >
                                {driverList.map((dl) => <Select.Option key={dl.id} value={dl.id}>{dl.name}</Select.Option>)}
                            </Select>,
                        )}
                    </Form.Item>
                    <Form.Item label="Division">
                        {getFieldDecorator('division_id', {
                            initialValue: videoSearchFilters?.search?.division_id || [],
                        })(
                            <Select
                                placeholder="Select Division"
                                filterOption={(input, option) => option.props.children.toLowerCase()
                                    .indexOf(input.toLowerCase()) >= 0}
                                mode="multiple"
                                style={{ width: '100%' }}
                                showSearch
                                allowClear={<Erase size={12} />}
                            >
                                {
                                    divisions.map((v, i) => (
                                    /* eslint-disable-next-line react/no-array-index-key */
                                        (<Select.Option key={`${i}`} value={v.id}>{v.name}</Select.Option>)
                                    ))
                                }

                            </Select>,
                        )}
                    </Form.Item>
                    <Form.Item label="Date">
                        {getFieldDecorator('date', {
                            initialValue: dayjs(videoSearchFilters?.search?.date) || dateInitialVal || dayjs(),
                        })(
                            <DatePicker 
                                format="DD/MM/YYYY" 
                                minDate={dayjs().subtract(videoRetentionDays, 'days')}
                                allowClear={false}
                                suffixIcon={<Calendar />}
                                maxDate={dayjs()}
                            />,
                        )}
                    </Form.Item>
                    <Row>
                        <Col xs={12}>
                            <Form.Item label="Time From">
                                {getFieldDecorator('time_from', {
                                    initialValue: videoSearchFilters?.search?.time_from || timeFromInitialVal || '09:00',
                                    rules: [
                                        {
                                            pattern: /^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/,
                                            message: 'Time from must be a valid time and be in the format HH:MM',
                                        },
                                        {
                                            required: true,
                                            message: 'Time from is required!',
                                        },
                                    ],
                                })(
                                    <Input placeholder='HH:MM' />
                                )}
                            </Form.Item>
                        </Col>
                        <Col xs={12}>
                            <Form.Item label="Time To">
                                {getFieldDecorator('time_to', {
                                    initialValue: videoSearchFilters?.search?.time_to || timeToInitialVal || '10:00',
                                    rules: [
                                        {
                                            pattern: /^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/,
                                            message: 'Time to must be a valid time and be in the format HH:MM',
                                        },
                                        {
                                            required: true,
                                            message: 'Time to is required!',
                                        },
                                    ],
                                })(
                                    <Input placeholder='HH:MM' />
                                )}
                            </Form.Item>
                        </Col>
                    </Row>
                    <div className="mt-2 mb-1 text-center">
                        <Button
                            htmlType="submit"
                            type="primary"
                        >
                            Filter
                        </Button>
                    </div>
                </Form>
            )
        );
    }
}

VideoSearchFilterForm.propTypes = {
    onSubmit: PropTypes.func.isRequired,
    actions: PropTypes.object.isRequired,
    divisions: PropTypes.array.isRequired,
    driverList: PropTypes.array.isRequired,
    mapDate: PropTypes.string,
    form: PropTypes.object,
};

const VideoFileListFilter = Form.create({ name: 'video_search_filter_form' })(VideoSearchFilterForm);

function mapStateToProps(state, ownProps) {
    return {
        ...ownProps,
        event_types: state.asset.event_types,
        divisions: state.asset.divisions,
        driverList: state.driver.driverList,
        mapDate: state.global.mapDate,
        assetOptions: state.asset.options,
        mapSearchLocationBoundary: state.video.mapSearchLocationBoundary,
        mapSearchVideoSelectedDate: state.video.mapSearchVideoSelectedDate,
        mapSearchVideoSliderValue: state.video.mapSearchVideoSliderValue,
        searchParams: state.video.historicVideosSearchParams,
        videoRetentionDays: state.user.userCompany.video_retention_days,
        videoSearchFilters: state.video.videoSearchFilters,
        mapSearchVideoMapMode: state.video.mapSearchVideoMapMode,
        mapSearchSelectedAssetId: state.video.mapSearchSelectedAssetId,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        actions: bindActionCreators({
            ...assetActions,
        }, dispatch),
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(VideoFileListFilter);
